import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import RefreshIcon from '@material-ui/icons/Refresh';
import { makeStyles } from '@material-ui/core/styles';

import {
  currencySign,
  explainFailure,
  moneyLabel,
  PRICING_CATEGORY_LABEL,
} from '../../../constants/whatsapp';
import { CONSENT_STATUS } from '../../../constants/whatsapp-contacts';
import { actions as whatsappActions } from '../actions';
import PeriodChart, { SERIES } from '../components/PeriodChart';
import { NAME as WHATSAPP_NAME } from '../reducer';
import { colors } from '../theme';
import { kyivNow, toKyivWall } from '../kyivTime';

function mapStateToProps(state) {
  return { ...state[WHATSAPP_NAME] };
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...whatsappActions }, dispatch);

const PERIODS = [
  { days: 7, label: '7 дней' },
  { days: 30, label: '30 дней' },
  { days: 90, label: '90 дней' },
];

const REFRESH_MS = 30000;
const MAX_RANGE_DAYS = 366;
const DAY_MS = 24 * 60 * 60 * 1000;

// Сегодняшняя дата по Киеву в виде 2026-10-16: так её понимает поле даты и сервер.
const todayIso = () => {
  const d = kyivNow();
  return `${d.getFullYear()}-${`0${d.getMonth() + 1}`.slice(-2)}-${`0${d.getDate()}`.slice(-2)}`;
};
const spanDays = (from, to) => (Date.parse(to) - Date.parse(from)) / DAY_MS + 1;

// Цвета статусов согласия совпадают со значками в списке контактов.
const CONSENT_COLORS = {
  confirmed: colors.green,
  pending: '#ffb300',
  declined: colors.red,
  none: '#b0bec5',
};
const CONSENT_ORDER = ['confirmed', 'pending', 'declined', 'none'];

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(3),
  },
  toolbarSpacer: { flexGrow: 1 },
  updated: {
    color: colors.textDim,
    fontSize: '12px',
    marginRight: theme.spacing(1),
  },
  dateField: { width: 150, marginLeft: 12 },
  rangeError: { color: '#b71c1c', fontSize: '12px', marginLeft: 12 },
  periodActive: {
    backgroundColor: colors.accent,
    color: '#fff',
    '&:hover': { backgroundColor: colors.accentHover },
  },
  tile: {
    padding: '18px 18px 16px',
    borderRadius: 10,
    height: '100%',
    borderTop: '4px solid transparent',
  },
  tileValue: {
    fontSize: '30px',
    fontWeight: 700,
    lineHeight: 1.1,
  },
  tileShare: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.textDim,
    marginLeft: 8,
  },
  tileLabel: {
    color: colors.text,
    fontSize: '13px',
    fontWeight: 600,
    marginTop: 8,
  },
  tileNote: {
    color: colors.textDim,
    fontSize: '12px',
    marginTop: 2,
  },
  panel: {
    padding: '20px 22px',
    borderRadius: 10,
    height: '100%',
  },
  panelTitle: {
    color: colors.text,
    fontWeight: 700,
    fontSize: '15px',
    marginBottom: 4,
  },
  panelHint: {
    color: colors.textDim,
    fontSize: '12px',
    marginBottom: 14,
  },
  legend: { display: 'flex', flexWrap: 'wrap', marginTop: 6 },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 18,
    color: colors.textDim,
    fontSize: '12px',
  },
  legendDash: { width: 16, height: 3, borderRadius: 2, marginRight: 7 },
  row: { marginBottom: 14 },
  rowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    fontSize: '13px',
    marginBottom: 5,
  },
  rowLabel: { color: colors.text, fontWeight: 500 },
  rowValue: { color: colors.text, fontWeight: 700 },
  rowShare: { color: colors.textDim, fontWeight: 500, marginLeft: 6 },
  track: {
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.panelAlt,
    overflow: 'hidden',
    display: 'flex',
  },
  fill: { height: '100%', transition: 'width 0.4s ease' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    color: colors.accent,
    fontSize: '12px',
    fontWeight: 600,
    textAlign: 'left',
    padding: '10px 8px',
    backgroundColor: colors.panelAlt,
    whiteSpace: 'nowrap',
  },
  thNum: { textAlign: 'right' },
  td: {
    color: colors.text,
    fontSize: '13px',
    padding: '12px 8px',
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'middle',
  },
  tdNum: { textAlign: 'right', whiteSpace: 'nowrap' },
  tdName: { maxWidth: 220, wordBreak: 'break-word' },
  templateName: { color: colors.textFaint, fontSize: '11px' },
  stack: {
    display: 'flex',
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: colors.panelAlt,
    minWidth: 90,
  },
  failureTitle: { color: colors.text, fontSize: '13px', fontWeight: 600 },
  failureHint: { color: colors.textDim, fontSize: '12px', marginTop: 2 },
  hours: {
    display: 'flex',
    alignItems: 'flex-end',
    height: 130,
    borderBottom: `1px solid ${colors.border}`,
  },
  hourCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: '100%',
    padding: '0 1.5px',
  },
  hourBar: { width: '100%', borderRadius: '3px 3px 0 0', minHeight: 2 },
  hourLabels: { display: 'flex', marginTop: 4 },
  hourLabel: {
    flex: 1,
    textAlign: 'center',
    color: colors.textDim,
    fontSize: '10px',
  },
  bestTime: {
    marginTop: 12,
    padding: '9px 12px',
    borderRadius: 8,
    backgroundColor: colors.accentSoft,
    color: colors.accent,
    fontSize: '13px',
    fontWeight: 600,
  },
  empty: {
    padding: '34px 20px',
    textAlign: 'center',
    color: colors.textDim,
    borderRadius: 10,
    marginBottom: theme.spacing(3),
  },
  emptyTitle: { color: colors.text, fontWeight: 700, fontSize: '16px', marginBottom: 6 },
  error: {
    padding: '16px 20px',
    borderRadius: 10,
    color: '#b71c1c',
    backgroundColor: '#ffebee',
    marginBottom: theme.spacing(3),
  },
  link: { color: colors.accent, fontWeight: 600, textDecoration: 'none', fontSize: '13px' },
  swatch: { width: 10, height: 10, borderRadius: 3, marginRight: 8, flexShrink: 0 },
  legendRow: { display: 'flex', alignItems: 'center', fontSize: '13px', padding: '5px 0' },
  legendRowName: { flexGrow: 1, color: colors.text },
  legendRowValue: { fontWeight: 700, color: colors.text },
}));

// 1 получатель, 2 получателя, 5 получателей.
const recipientsLabel = (n) => {
  const lastTwo = n % 100;
  const last = n % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return `${n} получателей`;
  if (last === 1) return `${n} получатель`;
  if (last >= 2 && last <= 4) return `${n} получателя`;
  return `${n} получателей`;
};

const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
const number = (n) => Number(n || 0).toLocaleString('ru-RU');
const money = (n) => `${Math.round(n).toLocaleString('ru-RU')} грн`;
const clock = (iso) => {
  const d = toKyivWall(iso);
  return `${`0${d.getHours()}`.slice(-2)}:${`0${d.getMinutes()}`.slice(-2)}`;
};

function Tile({ classes, color, value, share, label, note }) {
  return (
    <Paper className={classes.tile} style={{ borderTopColor: color }}>
      <div>
        <span className={classes.tileValue} style={{ color }}>
          {value}
        </span>
        {share !== undefined && <span className={classes.tileShare}>{share}</span>}
      </div>
      <div className={classes.tileLabel}>{label}</div>
      {note && <div className={classes.tileNote}>{note}</div>}
    </Paper>
  );
}

// Строка с полосой: подпись, число, доля и заполненность.
function BarRow({ classes, label, value, share, fraction, color }) {
  return (
    <div className={classes.row}>
      <div className={classes.rowTop}>
        <span className={classes.rowLabel}>{label}</span>
        <span>
          <span className={classes.rowValue}>{value}</span>
          {share && <span className={classes.rowShare}>{share}</span>}
        </span>
      </div>
      <div className={classes.track}>
        <div
          className={classes.fill}
          style={{ width: `${Math.max(0, Math.min(1, fraction)) * 100}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function Statistics(props) {
  const classes = useStyles();
  const { contacts, templates } = props;

  const [days, setDays] = useState(30);
  // Свой диапазон дат { from, to }; null, пока выбрана одна из кнопок 7/30/90 дней.
  const [range, setRange] = useState(null);
  const [draft, setDraft] = useState({ from: '', to: '' });
  const period = range || days;
  const periodKey = range ? `${range.from}..${range.to}` : String(days);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Only the newest request may fill the screen: a slow answer for a period the user
  // already left (or one that arrives after leaving the page) is ignored.
  const latest = useRef(0);

  const load = useCallback(
    async (wanted) => {
      latest.current += 1;
      const mine = latest.current;
      setLoading(true);
      const result = await props.fetchStats(wanted);
      if (mine !== latest.current) return;
      setLoading(false);
      if (result.ok) {
        setData(result.data);
        setError('');
      } else {
        setError(result.error);
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // The consent block counts the contacts of the base with what the server knows about their
  // consent: both are loaded here too, not only on the lists page.
  useEffect(() => {
    props.fetchTemplates();
    props.fetchCompanies();
    props.fetchConsents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Расход берётся у Meta, это отдельный запрос: он может не ответить, и статистика останется.
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState('');
  useEffect(() => {
    let stopped = false;
    setPricing(null);
    setPricingError('');
    props.fetchPricing(period).then((result) => {
      if (stopped) return;
      if (result.ok) setPricing(result.data);
      else setPricingError(result.error);
    });
    return () => {
      stopped = true;
    };
  }, [periodKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load(period);
    const timer = setInterval(() => load(period), REFRESH_MS);
    return () => {
      clearInterval(timer);
      latest.current += 1;
    };
  }, [periodKey, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickDays = (value) => {
    setRange(null);
    setDraft({ from: '', to: '' });
    setDays(value);
  };

  // Диапазон применяется, как только обе даты выбраны и они в порядке.
  const changeDraft = (next) => {
    setDraft(next);
    if (next.from && next.to && next.from <= next.to && spanDays(next.from, next.to) <= MAX_RANGE_DAYS) {
      setRange({ from: next.from, to: next.to });
    }
  };
  const draftError =
    draft.from && draft.to
      ? draft.from > draft.to
        ? 'Начало позже конца'
        : spanDays(draft.from, draft.to) > MAX_RANGE_DAYS
        ? `Не больше ${MAX_RANGE_DAYS} дней`
        : ''
      : '';

  const titleOf = useMemo(() => {
    const map = {};
    templates.forEach((item) => {
      map[item.templateName] = item.title;
    });
    return (name) => map[name] || name || 'Без названия';
  }, [templates]);

  const consent = useMemo(() => {
    const counts = { confirmed: 0, pending: 0, declined: 0, none: 0 };
    contacts.forEach((contact) => {
      counts[contact.consentStatus || 'none'] += 1;
    });
    return counts;
  }, [contacts]);

  // Одна и та же причина могла быть записана по-разному (с кодом в начале и без):
  // объединяем по тому, что видит менеджер.
  const failures = useMemo(() => {
    if (!data) return [];
    const merged = new Map();
    data.failures.forEach((item) => {
      const info = explainFailure(item.error);
      const known = merged.get(info.title);
      if (known) {
        known.count += item.count;
      } else {
        merged.set(info.title, { ...item, info });
      }
    });
    return Array.from(merged.values()).sort((a, b) => b.count - a.count);
  }, [data]);

  const totals = data ? data.totals : null;
  const hasActivity = Boolean(totals && (totals.sent > 0 || totals.replies > 0));

  const bestHour = useMemo(() => {
    if (!data) return null;
    const sum = data.hours.reduce((a, b) => a + b, 0);
    if (sum < 5) return null;
    const max = Math.max(...data.hours);
    return data.hours.indexOf(max);
  }, [data]);

  const hourMax = data ? Math.max(1, ...data.hours) : 1;
  const consentTotal = contacts.length;

  return (
    <div>
      <div className={classes.toolbar}>
        <ButtonGroup size="small" variant="outlined">
          {PERIODS.map((item) => (
            <Button
              key={item.days}
              className={!range && item.days === days ? classes.periodActive : ''}
              onClick={() => pickDays(item.days)}
            >
              {item.label}
            </Button>
          ))}
        </ButtonGroup>
        <TextField
          className={classes.dateField}
          type="date"
          size="small"
          variant="outlined"
          label="С"
          value={draft.from}
          error={Boolean(draftError)}
          onChange={(event) => changeDraft({ ...draft, from: event.target.value })}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: draft.to || todayIso() }}
        />
        <TextField
          className={classes.dateField}
          type="date"
          size="small"
          variant="outlined"
          label="По"
          value={draft.to}
          error={Boolean(draftError)}
          onChange={(event) => changeDraft({ ...draft, to: event.target.value })}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: draft.from || undefined, max: todayIso() }}
        />
        {draftError && <span className={classes.rangeError}>{draftError}</span>}
        <div className={classes.toolbarSpacer} />
        {data && <span className={classes.updated}>Обновлено в {clock(data.generatedAt)}</span>}
        <Tooltip title="Обновить">
          <IconButton size="small" onClick={() => load(period)} aria-label="Обновить">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {loading && !data && <LinearProgress style={{ marginBottom: 24 }} />}
      {error && <div className={classes.error}>{error}</div>}

      {data && !hasActivity && (
        <Paper className={classes.empty}>
          <div className={classes.emptyTitle}>За этот период рассылок не было</div>
          Как только вы отправите шаблон или клиенты начнут отвечать, здесь появятся цифры.
          Согласия клиентов видны ниже уже сейчас.
        </Paper>
      )}

      {data && (
        <>
          <Grid container spacing={3}>
            <Grid item xs={6} md={4} lg={2}>
              <Tile
                classes={classes}
                color={colors.accent}
                value={number(totals.sent)}
                label="Отправлено"
                note={recipientsLabel(totals.recipients)}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={2}>
              <Tile
                classes={classes}
                color={colors.green}
                value={number(totals.delivered)}
                share={`${pct(totals.delivered, totals.sent)}%`}
                label="Доставлено"
                note="от отправленных"
              />
            </Grid>
            <Grid item xs={6} md={4} lg={2}>
              <Tile
                classes={classes}
                color={colors.blue}
                value={number(totals.read)}
                share={`${pct(totals.read, totals.sent)}%`}
                label="Прочитано"
                note={`${pct(totals.read, totals.delivered)}% от доставленных`}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={2}>
              <Tile
                classes={classes}
                color="#8e24aa"
                value={number(totals.answered)}
                share={`${pct(totals.answered, totals.recipients)}%`}
                label="Ответили"
                note={`всего сообщений от клиентов: ${number(totals.replies)}`}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={2}>
              <Tile
                classes={classes}
                color={colors.red}
                value={number(totals.failed)}
                share={`${pct(totals.failed, totals.sent)}%`}
                label="Ошибки"
                note={totals.failed > 0 ? 'причины ниже' : 'всё дошло'}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={2}>
              <Tile
                classes={classes}
                color="#546e7a"
                value={
                  pricing
                    ? pricing.total.costUah != null
                      ? money(pricing.total.costUah)
                      : `${moneyLabel(pricing.total.cost)} ${currencySign(pricing.currency)}`
                    : pricingError
                    ? '—'
                    : '…'
                }
                label="Расход по данным Meta"
                note={
                  pricing
                    ? `${moneyLabel(pricing.total.cost)} ${currencySign(pricing.currency)} · платных сообщений: ${number(
                        pricing.total.volume
                      )}`
                    : pricingError || 'загружаем у Meta'
                }
              />
            </Grid>
          </Grid>

          {pricing && pricing.byCategory.length > 0 && (
            <Grid container spacing={3} style={{ marginTop: 4 }}>
              <Grid item xs={12}>
                <Paper className={classes.panel}>
                  <div className={classes.panelTitle}>На что ушли деньги</div>
                  <div className={classes.panelHint}>
                    Как считает Meta: платные сообщения по категориям
                    {pricing.uahRate
                      ? `, в гривнах по курсу НБУ ${moneyLabel(pricing.uahRate, 4)}${
                          pricing.uahRateDate ? ` на ${pricing.uahRateDate.split('-').reverse().join('.')}` : ''
                        }`
                      : ''}
                    .
                  </div>
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        <th className={classes.th}>Категория</th>
                        <th className={`${classes.th} ${classes.thNum}`}>Платных</th>
                        <th className={`${classes.th} ${classes.thNum}`}>Бесплатных</th>
                        <th className={`${classes.th} ${classes.thNum}`}>Цена за сообщение</th>
                        <th className={`${classes.th} ${classes.thNum}`}>Расход</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pricing.byCategory.map((entry) => {
                        const price = pricing.perMessage[entry.category];
                        return (
                          <tr key={entry.category}>
                            <td className={classes.td}>
                              {PRICING_CATEGORY_LABEL[entry.category] || entry.category}
                            </td>
                            <td className={`${classes.td} ${classes.tdNum}`}>{number(entry.chargedVolume)}</td>
                            <td className={`${classes.td} ${classes.tdNum}`}>{number(entry.freeVolume)}</td>
                            <td className={`${classes.td} ${classes.tdNum}`}>
                              {price
                                ? price.priceUah != null
                                  ? `${moneyLabel(price.priceUah)} грн`
                                  : `${moneyLabel(price.price, 4)} ${currencySign(pricing.currency)}`
                                : '—'}
                            </td>
                            <td className={`${classes.td} ${classes.tdNum}`}>
                              {entry.cost > 0
                                ? `${entry.costUah != null ? money(entry.costUah) : ''} (${moneyLabel(entry.cost)} ${currencySign(
                                    pricing.currency
                                  )})`
                                : 'бесплатно'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Grid container spacing={3} style={{ marginTop: 4 }}>
            <Grid item xs={12} lg={8}>
              <Paper className={classes.panel}>
                <div className={classes.panelTitle}>Динамика по дням</div>
                <div className={classes.panelHint}>
                  Сколько сообщений отправлено, дошло и прочитано каждый день
                </div>
                <PeriodChart data={data.daily} />
                <div className={classes.legend}>
                  {SERIES.map((s) => (
                    <div key={s.key} className={classes.legendItem}>
                      <span
                        className={classes.legendDash}
                        style={{
                          backgroundColor: s.dashed ? 'transparent' : s.color,
                          borderTop: s.dashed ? `3px dashed ${s.color}` : 'none',
                        }}
                      />
                      {s.label}
                    </div>
                  ))}
                </div>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper className={classes.panel}>
                <div className={classes.panelTitle}>Путь сообщения</div>
                <div className={classes.panelHint}>
                  Сколько дошло до каждого шага (ответившие считаются от получателей)
                </div>
                <BarRow
                  classes={classes}
                  label="Отправлено"
                  value={number(totals.sent)}
                  share="100%"
                  fraction={totals.sent > 0 ? 1 : 0}
                  color={colors.accent}
                />
                <BarRow
                  classes={classes}
                  label="Доставлено"
                  value={number(totals.delivered)}
                  share={`${pct(totals.delivered, totals.sent)}%`}
                  fraction={totals.sent ? totals.delivered / totals.sent : 0}
                  color={colors.green}
                />
                <BarRow
                  classes={classes}
                  label="Прочитано"
                  value={number(totals.read)}
                  share={`${pct(totals.read, totals.sent)}%`}
                  fraction={totals.sent ? totals.read / totals.sent : 0}
                  color={colors.blue}
                />
                <BarRow
                  classes={classes}
                  label="Ответили"
                  value={number(totals.answered)}
                  share={`${pct(totals.answered, totals.recipients)}%`}
                  fraction={totals.recipients ? totals.answered / totals.recipients : 0}
                  color="#8e24aa"
                />
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3} style={{ marginTop: 4 }}>
            <Grid item xs={12} lg={7}>
              <Paper className={classes.panel}>
                <div className={classes.panelTitle}>По шаблонам</div>
                <div className={classes.panelHint}>
                  Какие шаблоны доходят и читаются лучше всего
                </div>
                {data.templates.length === 0 ? (
                  <div className={classes.panelHint}>Пока ни один шаблон не отправлялся.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className={classes.table}>
                      <thead>
                        <tr>
                          <th className={classes.th}>Шаблон</th>
                          <th className={`${classes.th} ${classes.thNum}`}>Отпр.</th>
                          <th className={`${classes.th} ${classes.thNum}`}>Дост.</th>
                          <th className={`${classes.th} ${classes.thNum}`}>Проч.</th>
                          <th className={`${classes.th} ${classes.thNum}`}>Ош.</th>
                          <th className={classes.th} />
                        </tr>
                      </thead>
                      <tbody>
                        {data.templates.map((row) => {
                          const readOnly = row.read;
                          const deliveredOnly = row.delivered - row.read;
                          const waiting = row.sent - row.delivered - row.failed;
                          const part = (n) => `${(n / row.sent) * 100}%`;
                          return (
                            <tr key={row.name}>
                              <td className={`${classes.td} ${classes.tdName}`}>
                                {titleOf(row.name)}
                                <div className={classes.templateName}>{row.name}</div>
                              </td>
                              <td className={`${classes.td} ${classes.tdNum}`}>{number(row.sent)}</td>
                              <td className={`${classes.td} ${classes.tdNum}`}>
                                {number(row.delivered)}
                                <span className={classes.rowShare}>{pct(row.delivered, row.sent)}%</span>
                              </td>
                              <td className={`${classes.td} ${classes.tdNum}`}>
                                {number(row.read)}
                                <span className={classes.rowShare}>{pct(row.read, row.sent)}%</span>
                              </td>
                              <td className={`${classes.td} ${classes.tdNum}`}>{number(row.failed)}</td>
                              <td className={classes.td}>
                                <div className={classes.stack}>
                                  <div style={{ width: part(readOnly), backgroundColor: colors.blue }} />
                                  <div style={{ width: part(deliveredOnly), backgroundColor: colors.green }} />
                                  <div style={{ width: part(waiting), backgroundColor: '#cfd8dc' }} />
                                  <div style={{ width: part(row.failed), backgroundColor: colors.red }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper className={classes.panel}>
                <div className={classes.panelTitle}>Почему не дошло</div>
                <div className={classes.panelHint}>Причины ошибок, которые вернула Meta</div>
                {failures.length === 0 ? (
                  <div className={classes.panelHint}>Ошибок за этот период нет.</div>
                ) : (
                  failures.map((item) => {
                    const { info } = item;
                    return (
                      <div key={info.title} className={classes.row}>
                        <div className={classes.rowTop}>
                          <span className={classes.failureTitle}>{info.title}</span>
                          <span>
                            <span className={classes.rowValue}>{number(item.count)}</span>
                            <span className={classes.rowShare}>
                              {pct(item.count, totals.failed)}%
                            </span>
                          </span>
                        </div>
                        <div className={classes.track}>
                          <div
                            className={classes.fill}
                            style={{
                              width: `${pct(item.count, failures[0].count)}%`,
                              backgroundColor: colors.red,
                            }}
                          />
                        </div>
                        {info.hint && <div className={classes.failureHint}>{info.hint}</div>}
                      </div>
                    );
                  })
                )}
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3} style={{ marginTop: 4 }}>
            <Grid item xs={12} lg={6}>
              <Paper className={classes.panel}>
                <div className={classes.panelTitle}>Когда клиенты отвечают</div>
                <div className={classes.panelHint}>
                  Сообщения от клиентов по часам суток (киевское время): так видно, когда
                  лучше отправлять рассылку
                </div>
                <div className={classes.hours}>
                  {data.hours.map((count, hour) => (
                    <Tooltip key={hour} title={`${hour}:00 — ${count}`}>
                      <div className={classes.hourCol}>
                        <div
                          className={classes.hourBar}
                          style={{
                            height: `${(count / hourMax) * 100}%`,
                            backgroundColor: hour === bestHour ? colors.accent : '#9fa8da',
                            opacity: count === 0 ? 0.25 : 1,
                          }}
                        />
                      </div>
                    </Tooltip>
                  ))}
                </div>
                <div className={classes.hourLabels}>
                  {data.hours.map((count, hour) => (
                    <div key={hour} className={classes.hourLabel}>
                      {hour % 3 === 0 ? hour : ''}
                    </div>
                  ))}
                </div>
                {bestHour !== null ? (
                  <div className={classes.bestTime}>
                    Чаще всего пишут с {bestHour}:00 до {bestHour + 1}:00
                  </div>
                ) : (
                  <div className={classes.panelHint} style={{ marginTop: 12, marginBottom: 0 }}>
                    Пока мало ответов, чтобы назвать лучшее время (нужно хотя бы 5).
                  </div>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Paper className={classes.panel}>
                <div className={classes.panelTitle}>Согласия клиентов</div>
                <div className={classes.panelHint}>
                  Кому уже можно писать: {number(consent.confirmed)} из {number(consentTotal)}{' '}
                  контактов
                </div>
                <div className={classes.stack} style={{ height: 14, borderRadius: 7, marginBottom: 14 }}>
                  {CONSENT_ORDER.map((key) => (
                    <div
                      key={key}
                      style={{
                        width: `${pct(consent[key], consentTotal)}%`,
                        backgroundColor: CONSENT_COLORS[key],
                      }}
                    />
                  ))}
                </div>
                {CONSENT_ORDER.map((key) => (
                  <div key={key} className={classes.legendRow}>
                    <span className={classes.swatch} style={{ backgroundColor: CONSENT_COLORS[key] }} />
                    <span className={classes.legendRowName}>{CONSENT_STATUS[key].label}</span>
                    <span className={classes.legendRowValue}>{number(consent[key])}</span>
                    <span className={classes.rowShare} style={{ width: 44, textAlign: 'right' }}>
                      {pct(consent[key], consentTotal)}%
                    </span>
                  </div>
                ))}
                <div style={{ marginTop: 10 }}>
                  <Link to="/whatsapplists" className={classes.link}>
                    Открыть контакты и согласия →
                  </Link>
                </div>

                <div style={{ marginTop: 22 }}>
                  <BarRow
                    classes={classes}
                    label="Ответы текстом в этом месяце"
                    value={`${number(data.freeformMonth.used)}${
                      data.freeformMonth.limit > 0 ? ` из ${number(data.freeformMonth.limit)}` : ''
                    }`}
                    fraction={
                      data.freeformMonth.limit > 0
                        ? data.freeformMonth.used / data.freeformMonth.limit
                        : 0
                    }
                    color={
                      data.freeformMonth.limit > 0 &&
                      data.freeformMonth.used / data.freeformMonth.limit > 0.8
                        ? colors.red
                        : colors.accent
                    }
                  />
                  <div className={classes.panelHint} style={{ marginBottom: 0 }}>
                    Бесплатный лимит свободных ответов клиентам в окне 24 часа.
                  </div>
                </div>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(Statistics);
